import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, filter, Observable, of, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../../state/auth-model';
import { LocalStorageKeys } from '../constants/local-storage-keys';
import { AuthService } from '../services/auth.service';
import { LoggingService } from '../services/logging.service';
import { SelectedProfileService } from '../services/selected-profile.service';
import { SKIP_AUTH_HEADER } from './common';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_AUTH_HEADER) === true) {
    return next(req);
  }

  const authService = inject(AuthService);
  const selectedProfileService = inject(SelectedProfileService);
  const router = inject(Router);
  const loggingService = inject(LoggingService);

  return authService.isInitializing$.pipe(
    filter((isInitializing) => isInitializing === false),
    take(1),
    switchMap(() => {
      const user = authService.currentUser();
      const userRoles = authService.getUserRoles();

      if (!user || !userRoles) {
        if (environment.isLocal) {
          return next(req);
        }
        authService.clearClientSession();
        router.navigate(['/']);
        return throwError(() => new Error('User is not logged in or roles are missing'));
      }

      let activeUser$: Observable<User | null> = of(user);

      if (!environment.isLocal && user.sessionExpirationUtc) {
        const expirationMs = new Date(user.sessionExpirationUtc).getTime();
        const currentMs = Date.now();

        const diffInMin = (expirationMs - currentMs) / 60000;

        console.log(`The user session will expire after ${diffInMin} min of idle time.`);

        if (diffInMin <= 100) {
          console.log('Refreshing the session...');

          activeUser$ = authService.refreshSession().pipe(
            switchMap(() => authService.checkSession()),
            catchError((err) => {
              authService.currentUser.set(null);
              authService.clearClientSession();
              router.navigate(['/']);
              loggingService.logException('Session has expired and refresh failed.', 3, 'authInterceptor', { function: 'authService.refreshSession().pipe' });
              return throwError(() => new Error('Session has expired.'));
            }),
          );
        }

        if (currentMs >= expirationMs) {
          console.log('Local UI timer expired. Verifying with backend.');

          activeUser$ = authService.checkSession();
        }
      }

      return activeUser$.pipe(
        switchMap((verifiedUser) => {
          if (!verifiedUser) {
            console.warn('User session expired due to inactivity. Redirecting to login.');
            loggingService.logException('User session expired due to inactivity. Redirecting to login.', 3, 'authInterceptor', { function: 'activeUser$.pipe:verifiedUser' });
            authService.clearClientSession();
            router.navigate(['/']);
            return throwError(() => new Error('Session has expired due to inactivity.'));
          }

          const token = localStorage.getItem(LocalStorageKeys.token);

          const headers: Record<string, string> = {
            'X-User-Name': verifiedUser.userName,
            'X-User-Roles': verifiedUser.roles?.join(',') ?? userRoles,
          };

          if (selectedProfileService.selectedRecipientId()) {
            headers['X-Recipient-Id'] = selectedProfileService.selectedRecipientId()!.toString();
          }

          if (authService.isBusinessPartner() && verifiedUser.countyCodes?.length) {
            headers['X-User-County-Codes'] = verifiedUser.countyCodes.join(',');
          }

          if (selectedProfileService.selectedCountyCode()) {
            headers['X-Selected-profile-County-Code'] = selectedProfileService.selectedCountyCode()!;
          }

          if (selectedProfileService.selectedCountyId()) {
            headers['X-Selected-profile-County-Id'] = selectedProfileService.selectedCountyId()!.toString();
          }

          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          const modifiedReq = req.clone({
            setHeaders: headers,
          });

          return next(modifiedReq).pipe(
            catchError((error: HttpErrorResponse) => {
              // if (error.status === 401) {
              //   authService.currentUser.set(null);
              //   authService.clearClientSession();
              //   router.navigate(['/']);
              // }
              return throwError(() => {
                loggingService.logException(error, 3, 'authInterceptor', { function: 'next(modifiedReq).pipe', token: token });
                return error;
              });
            }),
          );
        }),
      );
    }),
  );
};
